import { useCallback } from 'react';

import { inIframe, sendToParent } from '@lyearn/core/utils/iframe';
import { TrackEvent } from '@/components/Analytics/hooks/types';
import useAnalytics from '@/components/Analytics/hooks/useAnalytics';
import { useBreadcrumbContext } from '@/components/Breadcrumb/context';
import { BREADCRUMB_QUERY_PARAM } from '@/components/Breadcrumb/hooks/useBreadcrumb';
import { useAppConfig } from '@/hooks/useAppConfig';
import useNavigation from '@/hooks/useNavigation';
import { getNavigationQuery } from '@/modules/corporate/utils/getNavigationQuery';
import corporateRoutes from '@/pages/corporate/routes';
import { courseDetailRoute } from '@/pages/corporate/routes/contents/course';
import { useRoleBasedNavigation } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import getSearchQuery from '../../utils/getSearchQuery';
import useGoToHome from '../useGoToHome';

export default function useCourseNavigation(
  courseId: string,
  onLessonChange?: (lessonId: string) => void,
) {
  const navigation = useNavigation();
  const { track } = useAnalytics();
  const { onBackToHome } = useGoToHome();
  const courseDetailsRoute = useRoleBasedNavigation(courseDetailRoute(courseId));
  const { ui } = useAppConfig();
  const { breadcrumbUrl } = useBreadcrumbContext();

  const navigate = useCallback(
    (
      _lessonPageId?: string | null,
      path?: string | null,
      state?: Record<string, string>,
      trackEvent?: TrackEvent,
    ) => {
      if (onLessonChange) {
        onLessonChange(_lessonPageId as string);
      } else {
        navigation.replace('', { query: { ...getSearchQuery(_lessonPageId, path), ...state } });
      }
      trackEvent && track(trackEvent.event, trackEvent.properties);
    },
    [navigation, onLessonChange, track],
  );

  const onBack = useCallback(
    (source?: string) => {
      if (inIframe() && !ui.isIFrameBackAllowed) {
        return sendToParent(JSON.stringify({ action: 'BackPressed', source: source }));
      }

      navigation.replace(courseDetailsRoute, {
        query: {
          [BREADCRUMB_QUERY_PARAM]: [...(breadcrumbUrl || [])],
        },
      });
      track('Course Back button Clicked');
    },
    [breadcrumbUrl, courseDetailsRoute, navigation, track, ui.isIFrameBackAllowed],
  );

  const onReviewStart = useCallback(() => {
    navigation.push(corporateRoutes.CourseReview('[courseId]'));
  }, [navigation]);

  const onBackToPath = useCallback(
    (pathId: string) => {
      const query = getNavigationQuery(breadcrumbUrl);
      navigation.push(corporateRoutes.PathDetails(pathId), { query });
    },
    [breadcrumbUrl, navigation],
  );

  return {
    navigate,
    onBack,
    onBackToHome,
    onReviewStart,
    onBackToPath,
  };
}
